import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PowerSettingsNew as PowerIcon,
  Campaign as CampaignIcon,
  Article as BlogIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import api from '../../utils/api';

interface RecruitmentCampaign {
  _id: string;
  name: string;
  isActive: boolean;
  openDate: string;
  closeDate: string;
  recruitmentMessage: string;
  underrepresentedCategories: string[];
  valuableExperienceAreas: string[];
  totalApplications?: number;
  createdAt: string;
  createdBy?: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface CampaignFormData {
  name: string;
  openDate: Date | null;
  closeDate: Date | null;
  recruitmentMessage: string;
  underrepresentedCategories: string[];
  valuableExperienceAreas: string[];
  isActive: boolean;
}

const WebsiteManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [campaigns, setCampaigns] = useState<RecruitmentCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<RecruitmentCampaign | null>(null);
  const [formData, setFormData] = useState<CampaignFormData>({
    name: '',
    openDate: null,
    closeDate: null,
    recruitmentMessage: 'We are currently recruiting trainee counsellors. Register your interest to join our team.',
    underrepresentedCategories: [],
    valuableExperienceAreas: [],
    isActive: false
  });
  
  // Available options from lookups
  const [availableCategories, setAvailableCategories] = useState<Array<{value: string, displayName: string}>>([]);
  const [availableExperience, setAvailableExperience] = useState<Array<{value: string, displayName: string}>>([]);

  useEffect(() => {
    loadCampaigns();
    loadLookups();
  }, []);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/website-management/campaigns');
      setCampaigns(response.data.campaigns);
      setError(null);
    } catch (err: any) {
      console.error('Error loading campaigns:', err);
      setError('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const loadLookups = async () => {
    try {
      // Fetch underrepresented categories
      const categoriesResponse = await api.get('/api/admin/lookups/underrepresentedCategories');
      setAvailableCategories(categoriesResponse.data || []);

      // Fetch experience/training options
      const experienceResponse = await api.get('/api/admin/lookups/experienceTraining');
      setAvailableExperience(experienceResponse.data || []);
    } catch (err: any) {
      console.error('Error loading lookups:', err);
      // Don't set error - lookups are optional
    }
  };

  const handleOpenDialog = (campaign?: RecruitmentCampaign) => {
    if (campaign) {
      setEditingCampaign(campaign);
      setFormData({
        name: campaign.name,
        openDate: new Date(campaign.openDate),
        closeDate: new Date(campaign.closeDate),
        recruitmentMessage: campaign.recruitmentMessage,
        underrepresentedCategories: campaign.underrepresentedCategories,
        valuableExperienceAreas: campaign.valuableExperienceAreas,
        isActive: campaign.isActive
      });
    } else {
      setEditingCampaign(null);
      setFormData({
        name: '',
        openDate: null,
        closeDate: null,
        recruitmentMessage: 'We are currently recruiting trainee counsellors. Register your interest to join our team.',
        underrepresentedCategories: [],
        valuableExperienceAreas: [],
        isActive: false
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCampaign(null);
  };

  const handleSaveCampaign = async () => {
    try {
      if (!formData.name || !formData.openDate || !formData.closeDate) {
        setError('Please fill in all required fields');
        return;
      }

      const campaignData = {
        ...formData,
        openDate: formData.openDate.toISOString(),
        closeDate: formData.closeDate.toISOString()
      };

      if (editingCampaign) {
        await api.put(`/api/website-management/campaigns/${editingCampaign._id}`, campaignData);
      } else {
        await api.post('/api/website-management/campaigns', campaignData);
      }

      await loadCampaigns();
      handleCloseDialog();
      setError(null);
    } catch (err: any) {
      console.error('Error saving campaign:', err);
      setError(err.response?.data?.error || 'Failed to save campaign');
    }
  };

  const handleDeleteCampaign = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) {
      return;
    }

    try {
      await api.delete(`/api/website-management/campaigns/${id}`);
      await loadCampaigns();
      setError(null);
    } catch (err: any) {
      console.error('Error deleting campaign:', err);
      setError(err.response?.data?.error || 'Failed to delete campaign');
    }
  };

  const handleToggleActive = async (id: string) => {
    try {
      await api.post(`/api/website-management/campaigns/${id}/toggle-active`);
      await loadCampaigns();
      setError(null);
    } catch (err: any) {
      console.error('Error toggling campaign:', err);
      setError(err.response?.data?.error || 'Failed to toggle campaign status');
    }
  };

  const toggleCategory = (categoryValue: string) => {
    if (formData.underrepresentedCategories.includes(categoryValue)) {
      // Remove it
      setFormData({
        ...formData,
        underrepresentedCategories: formData.underrepresentedCategories.filter(c => c !== categoryValue)
      });
    } else {
      // Add it
      setFormData({
        ...formData,
        underrepresentedCategories: [...formData.underrepresentedCategories, categoryValue]
      });
    }
  };

  const toggleExperience = (experienceValue: string) => {
    if (formData.valuableExperienceAreas.includes(experienceValue)) {
      // Remove it
      setFormData({
        ...formData,
        valuableExperienceAreas: formData.valuableExperienceAreas.filter(e => e !== experienceValue)
      });
    } else {
      // Add it
      setFormData({
        ...formData,
        valuableExperienceAreas: [...formData.valuableExperienceAreas, experienceValue]
      });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold' }}>
          Website Management
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        sx={{
          borderBottom: 1,
          borderColor: 'rgba(255, 255, 255, 0.3)',
          mb: 3,
          '& .MuiTab-root': {
            color: 'rgba(255, 255, 255, 0.9)',
            fontWeight: 500,
            '&.Mui-selected': { 
              color: '#ffffff',
              fontWeight: 600
            },
            '&:hover': {
              color: '#ffffff'
            }
          },
          '& .MuiTabs-indicator': { 
            backgroundColor: '#ffffff',
            height: 3
          }
        }}
      >
        <Tab icon={<CampaignIcon />} label="Recruitment Campaigns" iconPosition="start" />
        <Tab icon={<BlogIcon />} label="Blog Management" iconPosition="start" />
      </Tabs>

      {/* Tab 0: Recruitment Campaigns */}
      {activeTab === 0 && (
        <Box>
          <Box display="flex" justifyContent="flex-end" mb={3}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{
                backgroundColor: '#6366f1',
                '&:hover': { backgroundColor: '#4f46e5' }
              }}
            >
              Create Campaign
            </Button>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" py={8}>
              <CircularProgress />
            </Box>
          ) : campaigns.length === 0 ? (
            <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)' }}>
              <CardContent>
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center' }}>
                  No recruitment campaigns yet. Create your first campaign to get started.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <TableContainer
              component={Card}
              sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)' }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Campaign</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Open Date</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Close Date</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Applications</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {campaigns.map((campaign) => (
                    <TableRow key={campaign._id}>
                      <TableCell sx={{ color: 'white' }}>{campaign.name}</TableCell>
                      <TableCell>
                        <Chip
                          label={campaign.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          sx={{
                            backgroundColor: campaign.isActive ? '#4CAF50' : '#757575',
                            color: 'white'
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        {formatDate(campaign.openDate)}
                      </TableCell>
                      <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        {formatDate(campaign.closeDate)}
                      </TableCell>
                      <TableCell sx={{ color: 'white' }}>{campaign.totalApplications || 0}</TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleActive(campaign._id)}
                          sx={{ color: campaign.isActive ? '#4CAF50' : '#757575', mr: 1 }}
                          title={campaign.isActive ? 'Deactivate' : 'Activate'}
                        >
                          <PowerIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(campaign)}
                          sx={{ color: '#6366f1', mr: 1 }}
                          title="Edit"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteCampaign(campaign._id)}
                          sx={{ color: '#EF5350' }}
                          title="Delete"
                          disabled={campaign.totalApplications! > 0}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Tab 1: Blog Management */}
      {activeTab === 1 && (
        <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)' }}>
          <CardContent>
            <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
              Blog Management
            </Typography>
            <Alert severity="info">
              Blog management features coming soon. This will allow you to create, edit, and publish blog posts directly from the CRM.
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Campaign Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#1e3a8a',
            color: 'white'
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {editingCampaign ? 'Edit Campaign' : 'Create Campaign'}
            </Typography>
            <IconButton onClick={handleCloseDialog} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Campaign Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Spring 2026"
                required
                sx={{
                  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                    '&.Mui-focused fieldset': { borderColor: '#6366f1' }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label="Open Date"
                  value={formData.openDate}
                  onChange={(date) => setFormData({ ...formData, openDate: date })}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      sx: {
                        '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                        '& .MuiOutlinedInput-root': {
                          color: 'white',
                          '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                          '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' }
                        }
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label="Close Date"
                  value={formData.closeDate}
                  onChange={(date) => setFormData({ ...formData, closeDate: date })}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      sx: {
                        '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                        '& .MuiOutlinedInput-root': {
                          color: 'white',
                          '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                          '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' }
                        }
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Recruitment Message"
                value={formData.recruitmentMessage}
                onChange={(e) => setFormData({ ...formData, recruitmentMessage: e.target.value })}
                sx={{
                  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 1 }}>
                Underrepresented Categories (Desirables)
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 2, display: 'block' }}>
                Select from available categories (managed in Admin Panel → Lookups)
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {availableCategories.map((category) => (
                  <Chip
                    key={category.value}
                    label={category.displayName}
                    onClick={() => toggleCategory(category.value)}
                    sx={{
                      backgroundColor: formData.underrepresentedCategories.includes(category.value) ? '#6366f1' : 'rgba(255, 255, 255, 0.1)',
                      color: 'white',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: formData.underrepresentedCategories.includes(category.value) ? '#4f46e5' : 'rgba(255, 255, 255, 0.2)'
                      }
                    }}
                  />
                ))}
                {availableCategories.length === 0 && (
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontStyle: 'italic' }}>
                    No categories available. Add them in Admin Panel → Lookups → Underrepresented Categories
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 1 }}>
                Valuable Experience Areas
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 2, display: 'block' }}>
                Select from available experience areas (managed in Admin Panel → Lookups)
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {availableExperience.map((experience) => (
                  <Chip
                    key={experience.value}
                    label={experience.displayName}
                    onClick={() => toggleExperience(experience.value)}
                    sx={{
                      backgroundColor: formData.valuableExperienceAreas.includes(experience.value) ? '#81C784' : 'rgba(255, 255, 255, 0.1)',
                      color: 'white',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: formData.valuableExperienceAreas.includes(experience.value) ? '#66BB6A' : 'rgba(255, 255, 255, 0.2)'
                      }
                    }}
                  />
                ))}
                {availableExperience.length === 0 && (
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontStyle: 'italic' }}>
                    No experience areas available. Add them in Admin Panel → Lookups → Experience/Training
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#4CAF50' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4CAF50' }
                    }}
                  />
                }
                label="Set as Active Campaign"
                sx={{ color: 'white' }}
              />
              <Typography variant="caption" display="block" sx={{ color: 'rgba(255, 255, 255, 0.6)', ml: 4 }}>
                Only one campaign can be active at a time
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseDialog} sx={{ color: 'white' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveCampaign}
            variant="contained"
            sx={{ backgroundColor: '#6366f1', '&:hover': { backgroundColor: '#4f46e5' } }}
          >
            {editingCampaign ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WebsiteManagement;
